import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useApp, rememberLocalName } from '../context/AppContext';
import { useLanguage } from '../i18n/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { logout, updateUserProfile, saveSavedPlace } from '../services/firebase';
import { buildWhatsappUrl, buildTelUrl } from '../utils/support';
import { APP } from '../config/app';
import { hapticSelection } from '../utils/haptics';
import { isValidName, cleanName } from '../utils/name';
import PlacePinPicker from '../components/PlacePinPicker';
import SupportModal from '../components/SupportModal';
import DeleteAccountModal from '../components/DeleteAccountModal';
import useScrollLock from '../utils/useScrollLock';
import { registerAndroidBackHandler } from '../utils/androidBack';
import { openPlayStoreListing } from '../utils/playReview';

const emptyContact = () => ({ name: '', phone: '' });

/* ═════════════════════════════════════════════════════════════════
   Vector SVG Icons for 10/10 Profile Design System
   ═════════════════════════════════════════════════════════════════ */
const SunIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="5" fill="#f59e0b" fillOpacity="0.2" />
    <line x1="12" y1="1" x2="12" y2="3"/>
    <line x1="12" y1="21" x2="12" y2="23"/>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
    <line x1="1" y1="12" x2="3" y2="12"/>
    <line x1="21" y1="12" x2="23" y2="12"/>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
  </svg>
);

const MoonIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

const BellIcon = () => (
  <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>
    <span style={{
      position: 'absolute',
      top: 0,
      right: 0,
      width: 7,
      height: 7,
      borderRadius: '50%',
      backgroundColor: '#22c55e',
      boxShadow: '0 0 6px #22c55e',
    }} />
  </div>
);

const StarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="#F59E0B" aria-hidden="true">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);

const PhoneCallIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
);

const PinIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>
);

const HomeVectorIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);

const WorkBuildingIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="2" width="16" height="20" rx="2" ry="2"/>
    <line x1="9" y1="22" x2="9" y2="18"/>
    <line x1="15" y1="22" x2="15" y2="18"/>
    <line x1="18" y1="18" x2="6" y2="18"/>
    <line x1="8" y1="6" x2="8.01" y2="6"/>
    <line x1="16" y1="6" x2="16.01" y2="6"/>
    <line x1="12" y1="6" x2="12.01" y2="6"/>
    <line x1="8" y1="10" x2="8.01" y2="10"/>
    <line x1="16" y1="10" x2="16.01" y2="10"/>
    <line x1="12" y1="10" x2="12.01" y2="10"/>
    <line x1="8" y1="14" x2="8.01" y2="14"/>
    <line x1="16" y1="14" x2="16.01" y2="14"/>
    <line x1="12" y1="14" x2="12.01" y2="14"/>
  </svg>
);

const GlobeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10"/>
    <line x1="2" y1="12" x2="22" y2="12"/>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  </svg>
);

const ShieldUserIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    <circle cx="12" cy="10" r="3"/>
    <path d="M7 18a5 5 0 0 1 10 0"/>
  </svg>
);

const ShieldSecurityIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

const HeadsetIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 18v-6a9 9 0 0 1 18 0v6"/>
    <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
  </svg>
);

const ShieldLockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    <rect x="9" y="11" width="6" height="5" rx="1"/>
    <path d="M10 11V9a2 2 0 0 1 4 0v2"/>
  </svg>
);

const DocumentIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
    <line x1="10" y1="9" x2="8" y2="9"/>
  </svg>
);

const LogoutIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
    <line x1="10" y1="11" x2="10" y2="17"/>
    <line x1="14" y1="11" x2="14" y2="17"/>
  </svg>
);

const ChevronRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

export default function Profile() {
  const navigate = useNavigate();
  const { user, userProfile, refreshUserProfile, rides } = useApp();
  const { t, lang, setLang } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(userProfile?.name || '');
  const [saving, setSaving] = useState(false);
  const [contacts, setContacts] = useState([emptyContact()]);
  const [savingContacts, setSavingContacts] = useState(false);
  const [safetyModalOpen, setSafetyModalOpen] = useState(false);
  const [placesModalOpen, setPlacesModalOpen] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [pinTarget, setPinTarget] = useState(null);
  // SupportModal / DeleteAccountModal lock scroll themselves; these are inline.
  useScrollLock(showLogoutConfirm || placesModalOpen || safetyModalOpen);

  // Profile-owned overlays must consume Android back before the app-level
  // tab handler turns /profile into the Book tab.
  useEffect(() => registerAndroidBackHandler(() => {
    if (pinTarget) {
      setPinTarget(null);
      return true;
    }
    if (safetyModalOpen) {
      setSafetyModalOpen(false);
      return true;
    }
    if (placesModalOpen) {
      setPlacesModalOpen(false);
      return true;
    }
    if (showSupportModal) {
      setShowSupportModal(false);
      return true;
    }
    if (showDeleteModal) {
      setShowDeleteModal(false);
      return true;
    }
    if (showLogoutConfirm) {
      if (!loggingOut) setShowLogoutConfirm(false);
      return true;
    }
    return false;
  }), [pinTarget, safetyModalOpen, placesModalOpen, showSupportModal, showDeleteModal, showLogoutConfirm, loggingOut]);

  const confirmLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await logout();
    } catch (e) {
      console.warn('logout failed:', e?.message);
      setLoggingOut(false);
      setShowLogoutConfirm(false);
    }
  };

  useEffect(() => {
    const list = userProfile?.emergencyContacts;
    if (Array.isArray(list) && list.length) {
      setContacts(list.map((c) => ({
        name: c.name || '',
        phone: String(c.phone || '').replace(/\D/g, '').slice(-10),
      })));
      try { localStorage.setItem('mt_rides_emergency_nudge_done', '1'); } catch { /* */ }
    }
  }, [userProfile?.emergencyContacts]);

  const phone = userProfile?.phone || user?.phoneNumber || '—';
  const displayPhone = phone.replace('+91', '').trim() || phone;
  const home = userProfile?.savedPlaces?.home;
  const work = userProfile?.savedPlaces?.work;
  const initial = (userProfile?.name || 'P').trim().charAt(0).toUpperCase();
  const completedRides = userProfile?.completedRidesCount ?? userProfile?.totalCompletedRides ?? null;
  const ratingAvg = Number(userProfile?.ratingAvg);
  const ratingDisplay = Number.isFinite(ratingAvg) && ratingAvg > 0
    ? ratingAvg.toFixed(1)
    : '—';

  const handleSaveName = async (e) => {
    e?.preventDefault?.();
    if (!user?.uid) return;
    if (!isValidName(name)) {
      toast.error(t('login.nameInvalid'));
      return;
    }
    const cleaned = cleanName(name);
    setSaving(true);
    try {
      await updateUserProfile(user.uid, { name: cleaned, profileComplete: true });
      rememberLocalName(cleaned);
      await refreshUserProfile(user.uid, { name: cleaned, profileComplete: true });
      setEditing(false);
      toast.success(t('profile.nameSaved') || 'Name updated');
    } catch {
      toast.error(t('authErrors.default'));
    } finally {
      setSaving(false);
    }
  };

  const setPlace = async (key, place) => {
    if (!user?.uid || !place?.lat) {
      toast.error(t('places.needLocation'));
      return;
    }
    try {
      const saved = {
        name: place.name || place.address,
        address: place.address || place.name,
        lat: place.lat,
        lng: place.lng,
      };
      await saveSavedPlace(user.uid, key, saved);
      try {
        localStorage.setItem(key === 'home' ? 'mtl_saved_home' : 'mtl_saved_work', JSON.stringify(saved));
      } catch { /* */ }
      await refreshUserProfile(user.uid);
      setPinTarget(null);
      toast.success(t('places.saved'));
    } catch {
      toast.error(t('authErrors.default'));
    }
  };

  const clearPlace = async (key) => {
    if (!user?.uid) return;
    try {
      await saveSavedPlace(user.uid, key, null);
      await refreshUserProfile(user.uid);
    } catch {
      toast.error(t('authErrors.default'));
    }
  };

  const updateContact = (idx, field, value) => {
    setContacts((prev) => prev.map((c, i) => (
      i === idx
        ? {
            ...c,
            [field]: field === 'phone'
              ? value.replace(/\D/g, '').slice(0, 10)
              : value.slice(0, 40),
          }
        : c
    )));
  };

  const addContact = () => {
    if (contacts.length >= 3) return;
    setContacts((prev) => [...prev, emptyContact()]);
  };

  const removeContact = (idx) => {
    setContacts((prev) => (prev.length <= 1 ? [emptyContact()] : prev.filter((_, i) => i !== idx)));
  };

  const saveContacts = async () => {
    if (!user?.uid) return;
    const cleaned = contacts
      .map((c) => ({
        name: String(c.name || '').trim().slice(0, 40),
        phone: String(c.phone || '').replace(/\D/g, '').slice(-10),
      }))
      .filter((c) => c.phone.length === 10);
    for (const c of contacts) {
      if (c.phone && c.phone.length !== 10) {
        toast.error(t('safety.invalidPhone'));
        return;
      }
    }
    setSavingContacts(true);
    try {
      await updateUserProfile(user.uid, { emergencyContacts: cleaned });
      await refreshUserProfile(user.uid);
      setContacts(cleaned.length ? cleaned : [emptyContact()]);
      if (cleaned.length) {
        try { localStorage.setItem('mt_rides_emergency_nudge_done', '1'); } catch { /* */ }
      }
      toast.success(t('safety.saved'));
      setSafetyModalOpen(false);
    } catch {
      toast.error(t('authErrors.default'));
    } finally {
      setSavingContacts(false);
    }
  };

  if (pinTarget) {
    const existing = pinTarget === 'home' ? home : work;
    return (
      <PlacePinPicker
        title={pinTarget === 'home' ? t('places.setHomeMap') : t('places.setWorkMap')}
        confirmLabel={t('places.confirmPin')}
        initialPlace={existing}
        pinKind={pinTarget}
        onClose={() => setPinTarget(null)}
        onConfirm={(place) => setPlace(pinTarget, place)}
      />
    );
  }

  return (
    <div className="app profile-page-container">
      {/* 10/10 Profile Top Header */}
      <header className="profile-main-header">
        <div className="profile-header-text">
          <h1 className="profile-page-title">{t('profile.title') || 'Profile'}</h1>
          <p className="profile-page-subtitle">{t('profile.subtitle') || 'Your account & preferences'}</p>
        </div>
        {/* Header action buttons (Theme Toggle & Bell - commented out for now)
        <div className="profile-header-actions">
          <button
            type="button"
            className="profile-bell-btn"
            onClick={() => toast(t('profile.noNotifications') || 'No new notifications', { icon: '🔔' })}
            aria-label={t("profile.notifications")}
          >
            <BellIcon />
          </button>
        </div>
        */}
      </header>

      <div className="page-content profile-scroll-content">
        {/* 10/10 Emerald Hero Card */}
        <section className="profile-luxury-hero-card">
          <div className="hero-top-row">
            <div className="hero-avatar-wrap">
              <div className="hero-avatar-btn" aria-hidden>
                <div className="hero-avatar-fallback">{initial}</div>
              </div>
            </div>

            {/* Passenger Name, Verified Badge & Stats */}
            <div className="hero-info-col">
              <h2 className="hero-user-name">{userProfile?.name || 'Passenger'}</h2>
              <div className="hero-verified-badge">
                <span className="hero-verified-check">✓</span>
                <span>{t('profile.phoneVerified') || 'Phone verified'}</span>
              </div>
              <div className="hero-stats-row">
                <div className="hero-rating-box">
                  <StarIcon />
                  <span className="hero-rating-num">{ratingDisplay}</span>
                </div>
                {completedRides != null && <span className="hero-stat-sep">|</span>}
                  {completedRides != null && (
                    <span className="hero-rides-count">
                      {completedRides} {t('profile.ridesDone') || 'rides completed'}
                    </span>
                  )}
              </div>
            </div>
          </div>

          {/* Bottom Row: Phone Pill + Edit Profile Button */}
          <div className="hero-bottom-row">
            <div className="hero-phone-pill">
              <PhoneCallIcon />
              <span>+91 {displayPhone}</span>
            </div>
            <button
              type="button"
              className="hero-edit-btn"
              onClick={() => {
                setName(userProfile?.name || '');
                setEditing(true);
              }}
            >
              <span>{t('profile.editProfile') || 'Edit profile'}</span>
              <ChevronRight />
            </button>
          </div>
        </section>

        {/* ═════════════════════════════════════════════════════════
            Categorized Settings Group Cards (10/10 Layout)
            ═════════════════════════════════════════════════════════ */}

        {/* 1. ACCOUNT GROUP */}
        <div className="profile-section-block">
          <div className="profile-group-label">{t('profile.accountGroup') || 'ACCOUNT'}</div>
          <div className="profile-card-group">
            {/* Saved Places */}
            <div
              className="profile-menu-row"
              role="button"
              tabIndex={0}
              onClick={() => setPlacesModalOpen(true)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setPlacesModalOpen(true); }}
            >
              <div className="menu-icon-circle green">
                <PinIcon />
              </div>
              <div className="menu-text-col">
                <span className="menu-row-title">{t('places.title') || 'Saved places'}</span>
                <span className="menu-row-sub">{t('profile.savedPlacesSub') || 'Manage your saved locations'}</span>
              </div>
              <span className="menu-chevron"><ChevronRight /></span>
            </div>

            {/* Language */}
            <div
              className="profile-menu-row"
              role="button"
              tabIndex={0}
              onClick={() => setLang(lang === 'en' ? 'te' : 'en')}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setLang(lang === 'en' ? 'te' : 'en'); }}
            >
              <div className="menu-icon-circle green">
                <GlobeIcon />
              </div>
              <div className="menu-text-col">
                <span className="menu-row-title">{t('profile.language') || 'Language'}</span>
                <span className="menu-row-sub-active">{lang === 'en' ? 'English' : 'తెలుగు'}</span>
              </div>
              <span className="menu-chevron"><ChevronRight /></span>
            </div>

            <div
              className="profile-menu-row"
              role="button"
              tabIndex={0}
              onClick={() => { openPlayStoreListing(); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openPlayStoreListing(); }}
            >
              <div className="menu-icon-circle green">
                <StarIcon />
              </div>
              <div className="menu-text-col">
                <span className="menu-row-title">{t('profile.rateApp') || 'Rate MyTownly on Play Store'}</span>
                <span className="menu-row-sub">{t('profile.rateAppSub') || 'Share your experience with MyTownly'}</span>
              </div>
              <span className="menu-chevron"><ChevronRight /></span>
            </div>
          </div>
        </div>

        {/* 2. SAFETY GROUP */}
        <div className="profile-section-block">
          <div className="profile-group-label">{t('profile.safetyGroup') || 'SAFETY'}</div>
          <div className="profile-card-group">
            {/* Emergency Contacts */}
            <div
              className="profile-menu-row"
              role="button"
              tabIndex={0}
              onClick={() => setSafetyModalOpen(true)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSafetyModalOpen(true); }}
            >
              <div className="menu-icon-circle green">
                <ShieldUserIcon />
              </div>
              <div className="menu-text-col">
                <span className="menu-row-title">{t('safety.title') || 'Emergency contacts'}</span>
                <span className="menu-row-sub">{t('profile.emergencyContactsSub') || 'Add or manage your emergency contacts'}</span>
              </div>
              <span className="menu-chevron"><ChevronRight /></span>
            </div>

            {/* Safety Center */}
            <div
              className="profile-menu-row"
              role="button"
              tabIndex={0}
              onClick={() => navigate('/legal/safety')}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/legal/safety'); }}
            >
              <div className="menu-icon-circle green">
                <ShieldSecurityIcon />
              </div>
              <div className="menu-text-col">
                <span className="menu-row-title">{t('profile.safetyCenter') || 'Safety center'}</span>
                <span className="menu-row-sub">{t('profile.safetyCenterSub') || 'Safety tools, SOS, and ride security'}</span>
              </div>
              <span className="menu-chevron"><ChevronRight /></span>
            </div>
          </div>
        </div>

        {/* 3. SUPPORT GROUP */}
        <div className="profile-section-block">
          <div className="profile-group-label">{t('profile.supportGroup') || 'SUPPORT'}</div>
          <div className="profile-card-group">
            <div
              className="profile-menu-row"
              role="button"
              tabIndex={0}
              onClick={() => setShowSupportModal(true)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowSupportModal(true); }}
            >
              <div className="menu-icon-circle green">
                <HeadsetIcon />
              </div>
              <div className="menu-text-col">
                <span className="menu-row-title">{t('profile.helpSupport') || 'Help & support'}</span>
                <span className="menu-row-sub">{t('profile.helpSupportSub') || 'Get help with rides, payments or account'}</span>
              </div>
              <span className="menu-chevron"><ChevronRight /></span>
            </div>
          </div>
        </div>

        {/* 4. PRIVACY & LEGAL GROUP */}
        <div className="profile-section-block">
          <div className="profile-group-label">{t('profile.privacyLegalGroup') || 'PRIVACY & LEGAL'}</div>
          <div className="profile-card-group">
            {/* Privacy Policy */}
            <div
              className="profile-menu-row"
              role="button"
              tabIndex={0}
              onClick={() => navigate('/legal/privacy')}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/legal/privacy'); }}
            >
              <div className="menu-icon-circle green">
                <DocumentIcon />
              </div>
              <div className="menu-text-col">
                <span className="menu-row-title">{t('profile.privacy') || 'Privacy policy'}</span>
                <span className="menu-row-sub">{t('profile.privacyPolicySub') || 'How we collect and protect your data'}</span>
              </div>
              <span className="menu-chevron"><ChevronRight /></span>
            </div>

            {/* Terms of Service */}
            <div
              className="profile-menu-row"
              role="button"
              tabIndex={0}
              onClick={() => navigate('/legal/terms')}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/legal/terms'); }}
            >
              <div className="menu-icon-circle green">
                <DocumentIcon />
              </div>
              <div className="menu-text-col">
                <span className="menu-row-title">{t('profile.terms') || 'Terms of service'}</span>
                <span className="menu-row-sub">{t('profile.termsSub') || 'Rules, bookings and fare estimates'}</span>
              </div>
              <span className="menu-chevron"><ChevronRight /></span>
            </div>
          </div>
        </div>

        {/* 5. ACTIONS GROUP */}
        <div className="profile-section-block">
          <div className="profile-group-label">{t('profile.actionsGroup') || 'ACTIONS'}</div>
          <div className="profile-card-group">
            {/* Log out */}
            <div
              className="profile-menu-row"
              role="button"
              tabIndex={0}
              onClick={() => setShowLogoutConfirm(true)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowLogoutConfirm(true); }}
            >
              <div className="menu-icon-circle gray">
                <LogoutIcon />
              </div>
              <div className="menu-text-col">
                <span className="menu-row-title">{t('profile.logout') || 'Log out'}</span>
                <span className="menu-row-sub">{t('profile.logoutSub') || 'Sign out from your account'}</span>
              </div>
              <span className="menu-chevron"><ChevronRight /></span>
            </div>

            {/* Delete Account */}
            <div
              className="profile-menu-row"
              role="button"
              tabIndex={0}
              onClick={() => setShowDeleteModal(true)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowDeleteModal(true); }}
            >
              <div className="menu-icon-circle red">
                <TrashIcon />
              </div>
              <div className="menu-text-col">
                <span className="menu-row-title danger">{t('profile.deleteAccount') || 'Delete account'}</span>
                <span className="menu-row-sub">{t('profile.deleteAccountSub') || 'Permanently delete your account and data'}</span>
              </div>
              <span className="menu-chevron danger"><ChevronRight /></span>
            </div>
          </div>
        </div>

        <footer className="profile-app-footer">
          <p className="profile-app-version-tag">
            {APP.name} · v{APP.version}
          </p>
        </footer>
      </div>

      {/* ═════════════════════════════════════════════════════════
          Interactive Modals & Panels
          ═════════════════════════════════════════════════════════ */}

      {/* Edit Profile Name Modal */}
      {editing && (
        <div className="modal-backdrop" onClick={() => setEditing(false)}>
          <div className="modal-sheet" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">{t('profile.editName') || 'Edit Name'}</h3>
            <form onSubmit={handleSaveName} style={{ marginTop: '14px' }}>
              <input
                type="text"
                className="input-field"
                value={name}
                onChange={(e) => setName(e.target.value.replace(/[^\p{L}\p{M}\s.'-]/gu, '').slice(0, 40))}
                placeholder={t('login.namePlaceholder') || 'Enter your name'}
                autoFocus
                maxLength={40}
              />
              <div className="modal-actions" style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                <button
                  type="button"
                  className="btn btn-ghost"
                  style={{ flex: 1 }}
                  onClick={() => setEditing(false)}
                >
                  {t('common.cancel') || 'Cancel'}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ flex: 1 }}
                  disabled={saving || !isValidName(name)}
                >
                  {saving ? '…' : (t('common.save') || 'Save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 10/10 Saved Places Modal matching Reference */}
      {placesModalOpen && (
        <div className="modal-backdrop" onClick={() => setPlacesModalOpen(false)}>
          <div className="modal-sheet modal-card-reference" onClick={(e) => e.stopPropagation()}>
            <div className="ref-modal-header">
              <div className="ref-modal-icon-badge green">
                <PinIcon />
              </div>
              <div className="ref-modal-title-col">
                <h3 className="ref-modal-title">{t('places.title') || 'Saved Places'}</h3>
                <p className="ref-modal-subtitle">{t('places.mapHint') || 'Quick one-tap destinations for easy booking'}</p>
              </div>
              <button
                type="button"
                className="ref-modal-close-btn"
                onClick={() => setPlacesModalOpen(false)}
                aria-label={t('common.close')}
              >
                ✕
              </button>
            </div>

            {/* Info Banner */}
            <div className="ref-notice-card green">
              <span className="ref-notice-icon">💡</span>
              <p className="ref-notice-text">
                {t('places.shortcutTip')}
              </p>
            </div>

            {/* Saved Locations Section */}
            <div className="ref-section-group">
              <div className="ref-section-label">{t("places.savedLocations")}</div>
              
              {/* Home Place Card */}
              <div className="ref-place-card">
                <div className="ref-place-icon-box green">
                  <HomeVectorIcon />
                </div>
                <div className="ref-place-info">
                  <span className="ref-place-title">{t('places.home') || 'Home'}</span>
                  <span className="ref-place-address">{home?.address || t('places.notSet') || 'Not set'}</span>
                </div>
                <div className="ref-place-actions">
                  <button
                    type="button"
                    className="ref-btn-edit-pill"
                    onClick={() => {
                      setPlacesModalOpen(false);
                      setPinTarget('home');
                    }}
                  >
                    {home ? (t('places.update') || 'Update') : (t('places.setOnMap') || 'Set on map')}
                  </button>
                  {home && (
                    <button
                      type="button"
                      className="ref-btn-delete-icon"
                      onClick={() => clearPlace('home')}
                      aria-label={t("places.removeHome")}
                    >
                      <TrashIcon />
                    </button>
                  )}
                </div>
              </div>

              {/* Work Place Card */}
              <div className="ref-place-card">
                <div className="ref-place-icon-box green">
                  <WorkBuildingIcon />
                </div>
                <div className="ref-place-info">
                  <span className="ref-place-title">{t('places.work') || 'Work'}</span>
                  <span className="ref-place-address">{work?.address || t('places.notSet') || 'Not set'}</span>
                </div>
                <div className="ref-place-actions">
                  <button
                    type="button"
                    className="ref-btn-edit-pill"
                    onClick={() => {
                      setPlacesModalOpen(false);
                      setPinTarget('work');
                    }}
                  >
                    {work ? (t('places.update') || 'Update') : (t('places.setOnMap') || 'Set on map')}
                  </button>
                  {work && (
                    <button
                      type="button"
                      className="ref-btn-delete-icon"
                      onClick={() => clearPlace('work')}
                      aria-label={t("places.removeWork")}
                    >
                      <TrashIcon />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Action CTAs */}
            <div className="ref-modal-footer">
              <button
                type="button"
                className="ref-btn-primary"
                onClick={() => setPlacesModalOpen(false)}
              >
                {t('common.done') || 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 10/10 Emergency Contacts Modal matching Reference */}
      {safetyModalOpen && (
        <div className="modal-backdrop" onClick={() => setSafetyModalOpen(false)}>
          <div className="modal-sheet modal-card-reference" onClick={(e) => e.stopPropagation()}>
            <div className="ref-modal-header">
              <div className="ref-modal-icon-badge green">
                <ShieldUserIcon />
              </div>
              <div className="ref-modal-title-col">
                <h3 className="ref-modal-title">{t('safety.title') || 'Emergency Contacts'}</h3>
                <p className="ref-modal-subtitle">{t('safety.subtitle')}</p>
              </div>
              <button
                type="button"
                className="ref-modal-close-btn"
                onClick={() => setSafetyModalOpen(false)}
                aria-label={t('common.close')}
              >
                ✕
              </button>
            </div>

            {/* Safety Notice Banner */}
            <div className="ref-notice-card amber">
              <span className="ref-notice-icon">⚠️</span>
              <p className="ref-notice-text">
                {t("profile.emergencyProtectionNote")}
              </p>
            </div>

            {/* Trusted Contacts Section */}
            <div className="ref-section-group">
              <div className="ref-section-label">{t('profile.trustedContactsMax')}</div>
              
              {contacts.map((c, idx) => (
                <div key={idx} className="ref-contact-box">
                  <div className="ref-contact-box-header">
                    <span className="ref-contact-num">{t('profile.contactNum', { n: idx + 1 })}</span>
                    {contacts.length > 1 && (
                      <button
                        type="button"
                        className="ref-btn-remove-contact"
                        onClick={() => removeContact(idx)}
                      >
                        ✕ {t('profile.removeContact')}
                      </button>
                    )}
                  </div>
                  
                  {/* Name field */}
                  <div className="ref-input-wrap">
                    <span className="ref-input-label">{t("profile.contactName")}</span>
                    <input
                      type="text"
                      className="ref-input-text"
                      placeholder={t('safety.namePlaceholder') || 'e.g. Rahul / Sister'}
                      value={c.name}
                      onChange={(e) => updateContact(idx, 'name', e.target.value)}
                    />
                  </div>

                  {/* Phone field with +91 prefix pill */}
                  <div className="ref-input-wrap" style={{ marginTop: '8px' }}>
                    <span className="ref-input-label">{t("profile.registeredPhone")}</span>
                    <div className="ref-phone-group">
                      <span className="ref-phone-prefix">+91</span>
                      <input
                        type="tel"
                        inputMode="numeric"
                        maxLength={10}
                        className="ref-phone-input"
                        placeholder={t('safety.phonePlaceholder') || '10-digit mobile'}
                        value={c.phone}
                        onChange={(e) => updateContact(idx, 'phone', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))}

              {contacts.length < 3 && (
                <button
                  type="button"
                  className="ref-btn-add-dashed"
                  onClick={addContact}
                >
                  {t('profile.addContact')}
                </button>
              )}
            </div>

            {/* Action CTAs */}
            <div className="ref-modal-footer">
              <button
                type="button"
                className="ref-btn-primary"
                disabled={savingContacts}
                onClick={saveContacts}
              >
                {savingContacts ? t('safety.savingContacts') : (t('safety.save') || 'Save Emergency Contacts')}
              </button>
              <button
                type="button"
                className="ref-btn-secondary"
                onClick={() => setSafetyModalOpen(false)}
              >
                {t('common.cancel') || 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Support Modal */}
      <SupportModal
        open={showSupportModal}
        onClose={() => setShowSupportModal(false)}
        userProfile={userProfile}
        rides={rides}
        t={t}
      />

      {/* Delete Account Modal */}
      <DeleteAccountModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        userProfile={userProfile}
        user={user}
      />

      {/* Log out confirmation */}
      {showLogoutConfirm && (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={() => { if (!loggingOut) setShowLogoutConfirm(false); }}
        >
          <div
            className="modal-sheet logout-confirm-sheet"
            role="dialog"
            aria-modal="true"
            aria-label={t('profile.logoutConfirmTitle')}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="logout-confirm-grabber" aria-hidden="true" />
            <div className="logout-confirm-icon" aria-hidden="true">
              <LogoutIcon />
            </div>
            <div className="logout-confirm-copy">
              <h3>{t('profile.logoutConfirmTitle')}</h3>
              <p className="text-muted">{t('profile.logoutConfirmBody')}</p>
            </div>
            <div className="logout-confirm-actions">
              <button
                type="button"
                className="btn btn-primary logout-confirm-submit"
                disabled={loggingOut}
                onClick={confirmLogout}
              >
                {t('profile.logoutConfirmYes')}
              </button>
              <button
                type="button"
                className="btn btn-ghost logout-confirm-cancel"
                disabled={loggingOut}
                onClick={() => setShowLogoutConfirm(false)}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

